import { useEffect, type RefObject } from 'react';

/**
 * Calls `handler` when a pointer goes down anywhere outside every supplied
 * element.
 *
 * Uses `pointerdown` rather than `click` so a dropdown closes as soon as the
 * press begins, matching how native menus behave, and so it still fires when
 * the press ends outside the window.
 */
export function useClickOutside(
  refs: RefObject<HTMLElement | null>[],
  handler: () => void,
  enabled = true,
): void {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    function onPointerDown(event: PointerEvent): void {
      const target = event.target as Node | null;

      if (!target) {
        return;
      }

      const isInside = refs.some((ref) => ref.current?.contains(target));

      if (!isInside) {
        handler();
      }
    }

    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
    /* `refs` is a fresh array each render; its contents are stable, and the
       identity of the handler is what actually matters here. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handler, enabled]);
}
